/**
 * The following code is modified based on
 * https://github.com/puppeteer/puppeteer/blob/main/packages/puppeteer-core/src/common/USKeyboardLayout.ts
 *
 * Copyright 2017 Google Inc.
 * SPDX-License-Identifier: Apache-2.0
 * https://github.com/puppeteer/puppeteer/blob/main/LICENSE
 */
import type { KeyInput } from 'puppeteer-core';

/**
 * Only adapt for common macOS system hotkeys
 *
 * See issues:
 * - https://github.com/bytedance/UI-TARS-desktop/pull/560
 *
 * References:
 * - Mac shortcuts list: https://support.apple.com/zh-cn/102650
 * - Chrome: https://support.google.com/chrome/answer/157179
 * - CDP: https://chromedevtools.github.io/devtools-protocol/tot/Input/#method-dispatchKeyEvent
 * - Commands: https://source.chromium.org/chromium/chromium/src/+/main:third_party/blink/renderer/core/editing/commands/editor_command_names.h
 */
export const MAC_SYSTEM_HOTKEY_MAP = new Map<
  string,
  { key: KeyInput; commands: string }
>([
  ['CorM+A', { key: 'KeyA', commands: 'SelectAll' }],
  ['CorM+KeyA', { key: 'KeyA', commands: 'SelectAll' }],

  ['CorM+X', { key: 'KeyX', commands: 'Cut' }],
  ['CorM+KeyX', { key: 'KeyX', commands: 'Cut' }],

  ['CorM+C', { key: 'KeyC', commands: 'Copy' }],
  ['CorM+KeyC', { key: 'KeyC', commands: 'Copy' }],

  ['CorM+V', { key: 'KeyV', commands: 'Paste' }],
  ['CorM+KeyV', { key: 'KeyV', commands: 'Paste' }],

  ['CorM+Z', { key: 'KeyZ', commands: 'Undo' }],
  ['CorM+KeyZ', { key: 'KeyZ', commands: 'Undo' }],

  ['CorM+Y', { key: 'KeyY', commands: 'Redo' }],
  ['CorM+KeyY', { key: 'KeyY', commands: 'Redo' }],
  ['CorM+Shift+Z', { key: 'KeyZ', commands: 'Redo' }],
  ['CorM+Shift+KeyZ', { key: 'KeyZ', commands: 'Redo' }],
  ['Shift+CorM+Z', { key: 'KeyZ', commands: 'Redo' }],
  ['Shift+CorM+KeyZ', { key: 'KeyZ', commands: 'Redo' }],
]);

export const KEY_ABBR_TO_STANDARD_MAP: Readonly<Record<string, KeyInput>> = {
  // modifier keys
  ctrl: 'Control',
  cmd: 'Meta', // macOS only
  command: 'Meta', // macOS only
  opt: 'Alt', // macOS only
  option: 'Alt', // macOS only
  // arraw keys
  up: 'ArrowUp',
  down: 'ArrowDown',
  left: 'ArrowLeft',
  right: 'ArrowRight',
  // others
  esc: 'Escape',
  del: 'Delete',
  ins: 'Insert',
  pgup: 'PageUp',
  pgdown: 'PageDown',
  return: 'Enter',
};

// Auto-generated form puppeteer-core/src/common/USKeyboardLayout.ts _keyDefinitions
export const KEY_LOW_TO_STANDARD_MAP: Readonly<Record<string, KeyInput>> = {
  '0': '0',
  '1': '1',
  '2': '2',
  '3': '3',
  '4': '4',
  '5': '5',
  '6': '6',
  '7': '7',
  '8': '8',
  '9': '9',
  power: 'Power',
  eject: 'Eject',
  abort: 'Abort',
  help: 'Help',
  backspace: 'Backspace',
  tab: 'Tab',
  numpad5: 'Numpad5',
  numpadenter: 'NumpadEnter',
  enter: 'Enter',
  '\r': '\r',
  '\n': '\n',
  shiftleft: 'ShiftLeft',
  shiftright: 'ShiftRight',
  controlleft: 'ControlLeft',
  controlright: 'ControlRight',
  altleft: 'AltLeft',
  altright: 'AltRight',
  pause: 'Pause',
  capslock: 'CapsLock',
  escape: 'Escape',
  convert: 'Convert',
  nonconvert: 'NonConvert',
  space: 'Space',
  numpad9: 'Numpad9',
  pageup: 'PageUp',
  numpad3: 'Numpad3',
  pagedown: 'PageDown',
  end: 'End',
  numpad1: 'Numpad1',
  home: 'Home',
  numpad7: 'Numpad7',
  arrowleft: 'ArrowLeft',
  numpad4: 'Numpad4',
  numpad8: 'Numpad8',
  arrowup: 'ArrowUp',
  arrowright: 'ArrowRight',
  numpad6: 'Numpad6',
  numpad2: 'Numpad2',
  arrowdown: 'ArrowDown',
  select: 'Select',
  open: 'Open',
  printscreen: 'PrintScreen',
  insert: 'Insert',
  numpad0: 'Numpad0',
  delete: 'Delete',
  numpaddecimal: 'NumpadDecimal',
  digit0: 'Digit0',
  digit1: 'Digit1',
  digit2: 'Digit2',
  digit3: 'Digit3',
  digit4: 'Digit4',
  digit5: 'Digit5',
  digit6: 'Digit6',
  digit7: 'Digit7',
  digit8: 'Digit8',
  digit9: 'Digit9',
  keya: 'KeyA',
  keyb: 'KeyB',
  keyc: 'KeyC',
  keyd: 'KeyD',
  keye: 'KeyE',
  keyf: 'KeyF',
  keyg: 'KeyG',
  keyh: 'KeyH',
  keyi: 'KeyI',
  keyj: 'KeyJ',
  keyk: 'KeyK',
  keyl: 'KeyL',
  keym: 'KeyM',
  keyn: 'KeyN',
  keyo: 'KeyO',
  keyp: 'KeyP',
  keyq: 'KeyQ',
  keyr: 'KeyR',
  keys: 'KeyS',
  keyt: 'KeyT',
  keyu: 'KeyU',
  keyv: 'KeyV',
  keyw: 'KeyW',
  keyx: 'KeyX',
  keyy: 'KeyY',
  keyz: 'KeyZ',
  metaleft: 'MetaLeft',
  metaright: 'MetaRight',
  contextmenu: 'ContextMenu',
  numpadmultiply: 'NumpadMultiply',
  numpadadd: 'NumpadAdd',
  numpadsubtract: 'NumpadSubtract',
  numpaddivide: 'NumpadDivide',
  f1: 'F1',
  f2: 'F2',
  f3: 'F3',
  f4: 'F4',
  f5: 'F5',
  f6: 'F6',
  f7: 'F7',
  f8: 'F8',
  f9: 'F9',
  f10: 'F10',
  f11: 'F11',
  f12: 'F12',
  f13: 'F13',
  f14: 'F14',
  f15: 'F15',
  f16: 'F16',
  f17: 'F17',
  f18: 'F18',
  f19: 'F19',
  f20: 'F20',
  f21: 'F21',
  f22: 'F22',
  f23: 'F23',
  f24: 'F24',
  numlock: 'NumLock',
  scrolllock: 'ScrollLock',
  audiovolumemute: 'AudioVolumeMute',
  audiovolumedown: 'AudioVolumeDown',
  audiovolumeup: 'AudioVolumeUp',
  mediatracknext: 'MediaTrackNext',
  mediatrackprevious: 'MediaTrackPrevious',
  mediastop: 'MediaStop',
  mediaplaypause: 'MediaPlayPause',
  semicolon: 'Semicolon',
  equal: 'Equal',
  numpadequal: 'NumpadEqual',
  comma: 'Comma',
  minus: 'Minus',
  period: 'Period',
  slash: 'Slash',
  backquote: 'Backquote',
  bracketleft: 'BracketLeft',
  backslash: 'Backslash',
  bracketright: 'BracketRight',
  quote: 'Quote',
  altgraph: 'AltGraph',
  props: 'Props',
  cancel: 'Cancel',
  clear: 'Clear',
  shift: 'Shift',
  control: 'Control',
  alt: 'Alt',
  accept: 'Accept',
  modechange: 'ModeChange',
  ' ': ' ',
  print: 'Print',
  execute: 'Execute',
  '\u0000': '\u0000',
  a: 'A',
  b: 'B',
  c: 'C',
  d: 'D',
  e: 'E',
  f: 'F',
  g: 'G',
  h: 'H',
  i: 'I',
  j: 'J',
  k: 'K',
  l: 'L',
  m: 'M',
  n: 'N',
  o: 'O',
  p: 'P',
  q: 'Q',
  r: 'R',
  s: 'S',
  t: 'T',
  u: 'U',
  v: 'V',
  w: 'W',
  x: 'X',
  y: 'Y',
  z: 'Z',
  meta: 'Meta',
  '*': '*',
  '+': '+',
  '-': '-',
  '/': '/',
  ';': ';',
  '=': '=',
  ',': ',',
  '.': '.',
  '`': '`',
  '[': '[',
  '\\': '\\',
  ']': ']',
  "'": "'",
  attn: 'Attn',
  crsel: 'CrSel',
  exsel: 'ExSel',
  eraseeof: 'EraseEof',
  play: 'Play',
  zoomout: 'ZoomOut',
  ')': ')',
  '!': '!',
  '@': '@',
  '#': '#',
  $: '$',
  '%': '%',
  '^': '^',
  '&': '&',
  '(': '(',
  ':': ':',
  '<': '<',
  _: '_',
  '>': '>',
  '?': '?',
  '~': '~',
  '{': '{',
  '|': '|',
  '}': '}',
  '"': '"',
  softleft: 'SoftLeft',
  softright: 'SoftRight',
  camera: 'Camera',
  call: 'Call',
  endcall: 'EndCall',
  volumedown: 'VolumeDown',
  volumeup: 'VolumeUp',
};
